/**
 * PrScoreSlider
 *
 * The shared minimum-PR-score control rendered above the table on the three
 * score pages (All PR Articles, Needs Rereview, New Finds). It filters the list
 * to articles whose PR classifier score (`scorePRTopic`) is strictly greater
 * than the chosen value.
 *
 * The live value tracks the thumb while dragging for immediate feedback; the
 * committed value (which triggers a refetch) is only pushed up on release via
 * `onScoreCommit`, mirroring the original PR Bulk Review slider behaviour.
 */

import { useEffect, useState } from "react"
import { Slider } from "@/components/ui/slider"
import {
  PR_SCORE_SLIDER_MAX,
  PR_SCORE_SLIDER_STEP,
} from "@/components/pr-literature-review/usePrLiteratureReviewPage"

/**
 * Props for {@link PrScoreSlider}.
 */
interface PrScoreSliderProps {
  /** The committed minimum PR score (the applied slider value). */
  scoreMin: number
  /** The lower bound of the slider for the current page (0, or 0.5 on PR tabs). */
  scoreFloor: number
  /** Commit a new minimum PR score (fired on slider release). */
  onScoreCommit: (min: number) => void
}

/**
 * The minimum-PR-score slider control row.
 *
 * @param props - The slider props.
 * @returns The rendered control row.
 */
export default function PrScoreSlider({
  scoreMin,
  scoreFloor,
  onScoreCommit,
}: PrScoreSliderProps): React.JSX.Element {
  // Live slider value while dragging — drives the thumb and the ">" label for
  // immediate feedback. The committed value (which triggers a refetch) is only
  // pushed up on release via onValueCommit. Kept in sync with scoreMin whenever
  // that changes externally (page switch clamp, URL seed, reset).
  const [sliderValue, setSliderValue] = useState<number>(scoreMin)
  useEffect(() => {
    setSliderValue(scoreMin)
  }, [scoreMin])

  return (
    <div className="flex items-center justify-end gap-4 mb-4 flex-wrap">
      <div className="flex items-center gap-3 min-w-[260px]">
        <span className="text-sm text-muted-foreground whitespace-nowrap">
          PR score &gt; {sliderValue.toFixed(2)}
        </span>
        <Slider
          className="w-40 [&_[data-slot=slider-track]]:bg-yellow-400! [&_[data-slot=slider-range]]:bg-muted!"
          aria-label="Minimum PR score"
          min={scoreFloor}
          max={PR_SCORE_SLIDER_MAX}
          step={PR_SCORE_SLIDER_STEP}
          value={[sliderValue]}
          onValueChange={(values) => setSliderValue(values[0])}
          onValueCommit={(values) => onScoreCommit(values[0])}
        />
      </div>
    </div>
  )
}
